import React from 'react';
import { ReactNode } from 'react';
import { TranslatedContent } from 'libs/end-to-end/src/translation-content';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import cn from 'classnames';
import { InformationSuperscriptSvg } from 'apps/sigma2-ui/src/components/svg/InformationSuperscript';
import { TextWithNewlines } from 'apps/sigma2-ui/src/components/TextWithNewlines';

export const convertTranslatedContentToReactNode = (
  translatedContent: TranslatedContent,
  tooltipFn?: React.FC<React.ReactNode>,
): ReactNode => {
  if (typeof translatedContent === 'string') {
    return <TextWithNewlines text={translatedContent} />;
  }

  if (Array.isArray(translatedContent)) {
    const finalContent: ReactNode[] = [];

    let truncationGroupRun: ReactNode[] = [];
    let inTruncationGroup = false;
    let truncationGroupRunIteration = 0;

    for (let index = 0; index < translatedContent.length; index++) {
      const item = translatedContent[index];

      if (item.truncationGroupRunCount) {
        inTruncationGroup = true;
        truncationGroupRunIteration = 0;
        truncationGroupRun = [];
      }

      const textNodeEl = (
        <span
          key={index}
          title={item.tooltip ?? undefined}
          className={cn({
            'font-bold': item.isBold,
            'text-gray-900': item.color === 'black',
            'text-royal-600': item.color === 'blue',
            'text-purple-700': item.color === 'purple',
            'truncate flex-1':
              inTruncationGroup && truncationGroupRunIteration === 0,
            'decoration-solid underline': item.isDottedUnderline,
          })}
        >
          <TextWithNewlines text={item.text} />
          {item.isSuperscript && (
            <sup className={'pl-[2px]'}>
              <InformationSuperscriptSvg svg={{ className: 'inline' }} />
            </sup>
          )}
        </span>
      );

      const textNode =
        tooltipFn && item.tooltip ? tooltipFn(textNodeEl) : textNodeEl;

      if (inTruncationGroup) {
        truncationGroupRun.push(textNode);
        truncationGroupRunIteration++;

        if (truncationGroupRunIteration === item.truncationGroupRunCount) {
          inTruncationGroup = false;
          finalContent.push(
            <span className={'flex flex-nowrap'} key={`truncation-${index}`}>
              {truncationGroupRun}
            </span>,
          );
        }

        continue;
      }

      finalContent.push(textNode);
    }

    if (inTruncationGroup) {
      finalContent.push(
        <span className={'flex flex-nowrap'} key={`truncation-end`}>
          {truncationGroupRun}
        </span>,
      );
    }

    return finalContent;
  }

  throw guardExhaustive(translatedContent);
};
